import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { User, TaskType, PatientMeeting, MeetingType } from '../../types/api';
import apiService from '../../services/api';
import { getMeetingTypeName } from '../../utils/meetingUtils';

interface CreateTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientId: number;
    users: User[];
    patientDoctorId?: number;
    patientCaseManagerId?: number;
    taskTypes?: TaskType[]; // Optional to avoid breaking existing usage
    meetingTypes?: MeetingType[]; // Optional to avoid breaking existing usage
}

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
    isOpen,
    onClose,
    patientId,
    users,
    patientDoctorId,
    patientCaseManagerId,
    taskTypes: propsTaskTypes,
    meetingTypes: propsMeetingTypes,
}) => {
    const [taskTypes, setTaskTypes] = useState<TaskType[]>(propsTaskTypes || []);
    const [selectedTaskType, setSelectedTaskType] = useState<TaskType | null>(null);
    const [assignTo, setAssignTo] = useState<number[]>([]);
    const [priority, setPriority] = useState<string>('Low');
    const [description, setDescription] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Meeting dropdown state
    const [meetings, setMeetings] = useState<PatientMeeting[]>([]);
    const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>(propsMeetingTypes || []);
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
    const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);

    // Searchable dropdown state
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const token = localStorage.getItem('token') || '';

    // Filter to only active users
    const activeUsers = users.filter(user => user.active !== false);

    // Update taskTypes when prop changes
    useEffect(() => {
        if (propsTaskTypes) {
            setTaskTypes(propsTaskTypes);
        }
    }, [propsTaskTypes]);

    // Update meetingTypes when prop changes
    useEffect(() => {
        if (propsMeetingTypes) {
            setMeetingTypes(propsMeetingTypes);
        }
    }, [propsMeetingTypes]);

    // Fetch task types and meeting types when modal opens (only if not provided via props)
    useEffect(() => {
        if (isOpen && token) {
            const fetchData = async () => {
                setIsLoading(true);
                setError(null);
                try {
                    const promises: Promise<any>[] = [];

                    // Only fetch if not provided via props
                    if (!propsTaskTypes || propsTaskTypes.length === 0) {
                        promises.push(apiService.getTaskTypes(token));
                    }
                    if (!propsMeetingTypes || propsMeetingTypes.length === 0) {
                        promises.push(apiService.getMeetingTypes(token, true));
                    }

                    if (promises.length > 0) {
                        const results = await Promise.all(promises);

                        let resultIndex = 0;
                        if (!propsTaskTypes || propsTaskTypes.length === 0) {
                            setTaskTypes(results[resultIndex]);
                            resultIndex++;
                        }
                        if (!propsMeetingTypes || propsMeetingTypes.length === 0) {
                            setMeetingTypes(results[resultIndex]);
                        }
                    }
                } catch (err: any) {
                    console.error('Failed to load data:', err);
                    setError('Failed to load data');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        }
    }, [isOpen, token, propsTaskTypes, propsMeetingTypes]);

    // Fetch meetings when task type with meeting_drop_down is selected
    useEffect(() => {
        if (selectedTaskType?.meeting_drop_down && token && patientId) {
            const fetchMeetings = async () => {
                setIsLoadingMeetings(true);
                try {
                    const patientMeetings = await apiService.getMeetingsForPatient(token, patientId);
                    // Filter to only done meetings
                    const doneMeetings = patientMeetings.filter(m => m.status === 'done');
                    setMeetings(doneMeetings);
                } catch (err: any) {
                    console.error('Failed to load meetings:', err);
                } finally {
                    setIsLoadingMeetings(false);
                }
            };
            fetchMeetings();
        } else {
            setMeetings([]);
            setSelectedMeetingId(null);
        }
    }, [selectedTaskType, token, patientId]);

    // Auto-populate assignees when task type changes
    useEffect(() => {
        if (!selectedTaskType) return;

        // If default_assign has users, use them
        if (selectedTaskType.default_assign.length > 0) {
            setAssignTo(selectedTaskType.default_assign);
        }
        // If default_assign is empty, use assign_type logic
        else if (selectedTaskType.assign_type === 'doctor' && patientDoctorId) {
            setAssignTo([patientDoctorId]);
        }
        else if (selectedTaskType.assign_type === 'case_manager' && patientCaseManagerId) {
            setAssignTo([patientCaseManagerId]);
        }
        // Otherwise, clear assignees
        else {
            setAssignTo([]);
        }
    }, [selectedTaskType, patientDoctorId, patientCaseManagerId]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedTaskType(null);
            setAssignTo([]);
            setPriority('Low');
            setDescription('');
            setError(null);
            setSearchQuery('');
            setIsDropdownOpen(false);
            setMeetings([]);
            setSelectedMeetingId(null);
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isDropdownOpen]);

    const handleTaskTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const typeId = parseInt(e.target.value, 10);
        const taskType = taskTypes.find(t => t.id === typeId) || null;
        setSelectedTaskType(taskType);
    };

    const toggleUserSelection = (userId: number) => {
        setAssignTo(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const removeUser = (userId: number) => {
        setAssignTo(prev => prev.filter(id => id !== userId));
    };

    // Filter and sort users: selected first, then filtered by search
    const getFilteredUsers = () => {
        const query = searchQuery.toLowerCase();
        const filtered = activeUsers.filter(user => {
            const name = user.name?.toLowerCase() || '';
            const email = user.email?.toLowerCase() || '';
            return name.includes(query) || email.includes(query);
        });

        // Sort: selected users first, then alphabetically
        return filtered.sort((a, b) => {
            const aSelected = assignTo.includes(Number(a.id));
            const bSelected = assignTo.includes(Number(b.id));

            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;

            const aName = a.name || a.email || '';
            const bName = b.name || b.email || '';
            return aName.localeCompare(bName);
        });
    };

    const getSelectedUsers = () => {
        return activeUsers.filter(user => assignTo.includes(Number(user.id)));
    };

    const handleSubmit = async () => {
        setError(null);

        // Validation
        if (!selectedTaskType) {
            setError('Please select a task type');
            return;
        }
        if (assignTo.length === 0) {
            setError('Please assign to at least one user');
            return;
        }
        if (!description.trim()) {
            setError('Please enter a description');
            return;
        }
        if (selectedTaskType.meeting_drop_down && !selectedMeetingId) {
            setError('Please select a meeting');
            return;
        }

        setIsSaving(true);
        try {
            const payload: any = {
                token,
                patient_id: patientId,
                type: selectedTaskType.type,
                assign_to: assignTo,
                priority,
                status: 'New',
                description: description.trim(),
            };

            // Add meeting_id if selected
            if (selectedMeetingId) {
                payload.meeting_id = selectedMeetingId;
            }

            console.log('[CreateTask] Sending payload:', payload);
            await apiService.createInternalTask(payload);
            onClose();
        } catch (err: any) {
            console.error('Failed to create task:', err);
            setError(err.response?.data?.message || 'Failed to create task');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        onClose();
    };

    if (!isOpen) return null;

    const filteredUsers = getFilteredUsers();
    const selectedUsers = getSelectedUsers();

    return (
        <ModalWrapper modalId="CreateTaskModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Create Task"
            isLoading={isSaving}
            width="max-w-lg"
        >
            <div className="space-y-4 mb-6">
                {/* Subtitle */}
                <p className="text-sm text-[#366A75]">Create an internal task related to this patient.</p>

                {/* Task Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Task Type <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={selectedTaskType?.id || ''}
                        onChange={handleTaskTypeChange}
                        className={POPUP_STYLES.input}
                        disabled={isLoading || isSaving}
                    >
                        <option value="">Select a task type</option>
                        {taskTypes.map(type => (
                            <option key={type.id} value={type.id}>
                                {type.type}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Assign To - Searchable Dropdown */}
                <div ref={dropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Assign To <span className="text-red-500">*</span>
                    </label>

                    {/* Selected Users Display */}
                    {selectedUsers.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2">
                            {selectedUsers.map(user => (
                                <div
                                    key={user.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-[#366A75] text-white rounded-md text-sm"
                                >
                                    <span>{user.name || user.email}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeUser(Number(user.id))}
                                        disabled={isSaving}
                                        className="hover:text-red-200 transition-colors"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Search Input */}
                    <div className="relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setIsDropdownOpen(true)}
                            placeholder="Search and select users..."
                            className={POPUP_STYLES.input}
                            disabled={isSaving}
                        />

                        {/* Dropdown */}
                        {isDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-[#B4D9D4] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {filteredUsers.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500">
                                        No active users found
                                    </div>
                                ) : (
                                    <div className="py-1">
                                        {filteredUsers.map(user => {
                                            const isSelected = assignTo.includes(Number(user.id));
                                            return (
                                                <button
                                                    data-ai-id="task-assignee-option"
                                                    key={user.id}
                                                    type="button"
                                                    onClick={() => toggleUserSelection(Number(user.id))}
                                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-[#EBF5F5] transition-colors flex items-center justify-between ${isSelected ? 'bg-[#EBF5F5] font-medium' : ''
                                                        }`}
                                                >
                                                    <span className="text-gray-700">
                                                        {user.name || user.email}
                                                    </span>
                                                    {isSelected && (
                                                        <span className="text-[#366A75]">✓</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Meeting Dropdown - Conditionally shown */}
                {selectedTaskType?.meeting_drop_down && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Meeting <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedMeetingId || ''}
                            onChange={(e) => setSelectedMeetingId(e.target.value ? Number(e.target.value) : null)}
                            className={POPUP_STYLES.input}
                            disabled={isSaving || isLoadingMeetings}
                        >
                            <option value="">
                                {isLoadingMeetings ? 'Loading meetings...' : 'Select a meeting'}
                            </option>
                            {meetings.map(meeting => {
                                // Format date as dd/mm/yyyy
                                const meetingDate = new Date(meeting.start);
                                const formattedDate = `${String(meetingDate.getDate()).padStart(2, '0')}/${String(meetingDate.getMonth() + 1).padStart(2, '0')}/${meetingDate.getFullYear()}`;

                                // Get meeting type name using utility that handles re_intake
                                const meetingTypeName = getMeetingTypeName(meeting, meetingTypes);

                                return (
                                    <option key={meeting.id} value={meeting.id}>
                                        {formattedDate} {meetingTypeName}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}

                {/* Priority */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Priority <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className={POPUP_STYLES.input}
                        disabled={isSaving}
                    >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                    </select>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Add additional context (optional)"
                        rows={4}
                        className={`${POPUP_STYLES.input} resize-none`}
                        disabled={isSaving}
                    />
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                <button
                    onClick={handleSubmit}
                    disabled={isSaving || isLoading}
                    className={POPUP_STYLES.button}
                >
                    {isSaving ? 'Creating...' : 'Create Task'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
